import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.4, 0, 0.2, 1] },
  viewport: { once: true, margin: '-60px' },
});

export default function Access() {
  const { t, i18n } = useTranslation();
  const isJa = i18n.language?.startsWith('ja');

  const ADDRESS_JA = '〒437-1435 静岡県掛川市上土方嶺向902';
  const ADDRESS_EN = '902 Kamihijikata Minemukai, Kakegawa, Shizuoka 437-1435';

  // Direct embed URL built from the shared link coordinates
  const EMBED_URL =
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1638.0!2d137.9553!3d34.7095!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x601a057d65252a01%3A0x7e3b9e8d5b6f4e2a!2z5o6o5bed44OQ44Kw44O8!5e0!3m2!1sja!2sjp!4v1700000000000!5m2!1sja!2sjp&q=%E9%9D%99%E5%B2%A1%E7%9C%8C%E6%8E%9B%E5%B7%9D%E5%B8%82%E4%B8%8A%E5%9C%9F%E6%96%B9%E5%B6%BA%E5%90%91902';

  const MAPS_URL =
    'https://maps.app.goo.gl/ZnJz6DDX991NDdmy9';

  return (
    <section
      id="access"
      className="relative py-20 overflow-hidden bg-[#f4f9ee]"
    >
      {/* background dot pattern */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle,rgba(31,77,43,0.04) 1px,transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
      <div className="absolute top-10 right-10 text-[120px] opacity-[0.04] pointer-events-none select-none leading-none">🌿</div>
      <div className="absolute bottom-10 left-10 text-[100px] opacity-[0.04] pointer-events-none select-none leading-none">🌲</div>

      <div className="max-w-6xl mx-auto px-5 relative z-10">

        {/* HEADER */}
        <motion.div className="text-center mb-12" {...fadeUp()}>
          <div className="inline-flex items-center gap-2 bg-[#5b3a1c] text-white px-7 py-3 rounded-2xl shadow-lg border-2 border-[#7a5230] mb-5">
            <span className="text-xl">📍</span>
            <span className="font-extrabold text-lg tracking-wide">
              {isJa ? 'アクセス' : 'Access'}
            </span>
          </div>

          <h2
            className="font-extrabold text-[#1a3d20] mb-3"
            style={{ fontSize: 'clamp(2rem,5vw,3.5rem)', lineHeight: 1.1 }}
          >
            {t('access.title')}
          </h2>

          <p className="text-[#2d6b38]/70 text-base font-semibold max-w-md mx-auto">
            {t('access.subtitle')}
          </p>
        </motion.div>

        {/* MAIN CARD */}
        <motion.div
          {...fadeUp(0.1)}
          className="bg-white/75 backdrop-blur-xl rounded-[28px] border border-[#dfe7d5] overflow-hidden shadow-xl shadow-[#1f4d2b]/5 mb-6"
        >
          <div className="grid lg:grid-cols-2">

            {/* LEFT — Google Maps embed */}
            <div className="relative min-h-[420px] overflow-hidden">

              {/* map iframe fills the entire panel */}
              <iframe
                title="Kakegawa Buggy Park"
                src={EMBED_URL}
                className="absolute inset-0 w-full h-full border-0"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />

              {/* bottom gradient so the card info reads over the map */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#173116]/85 via-[#173116]/20 to-transparent pointer-events-none" />

              {/* content on top of map */}
              <div className="relative z-10 h-full flex flex-col justify-between p-8">
                <div>
                  <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 text-white rounded-full px-5 py-2 text-xs font-extrabold tracking-[.2em] uppercase mb-5">
                    Kakegawa Outdoor Park
                  </div>

                  <h3 className="text-white font-black text-[40px] leading-[1.1] mb-5">
                    KAKEGAWA<br />BUGGY PARK
                  </h3>

                  <p className="text-white/75 text-sm leading-[1.9] max-w-sm">
                    {isJa
                      ? '自然に囲まれたアウトドア体験施設。バギー・アクティビティ・キャンプ体験を楽しめます。'
                      : 'Outdoor adventure park surrounded by nature with buggy rides and unique experiences.'}
                  </p>
                </div>

                <a
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-white text-[#173116] hover:bg-[#ffd76a] font-extrabold text-sm px-6 py-4 rounded-2xl transition-all shadow-xl w-fit no-underline"
                >
                  📍 {isJa ? 'Googleマップで見る' : 'Open Google Maps'}
                </a>
              </div>
            </div>

            {/* RIGHT — info */}
            <div className="p-8 lg:p-10 bg-[#fafcf7]">
              <div className="grid gap-4">

                {/* ADDRESS */}
                <div className="bg-white border border-[#dfe7d5] rounded-[24px] p-6 shadow-sm">
                  <p className="text-[10px] font-extrabold tracking-[.22em] uppercase text-[#1a3d20]/40 mb-3">
                    {isJa ? '住所' : 'Address'}
                  </p>
                  <p className="text-[#1a3d20] font-extrabold text-[22px] leading-[1.7]">
                    {isJa ? ADDRESS_JA : ADDRESS_EN}
                  </p>
                </div>

                {/* CAR */}
                <div className="bg-white border border-[#dfe7d5] rounded-[24px] p-6 shadow-sm">
                  <p className="text-[10px] font-extrabold tracking-[.22em] uppercase text-[#1a3d20]/40 mb-4">
                    {isJa ? '車でお越しの方' : 'By Car'}
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between bg-[#f4f9ee] border border-[#e2ead8] rounded-2xl px-5 py-4">
                      <span className="font-extrabold text-[#1a3d20] text-lg">
                        {t('access.kakegawa_ic')}
                      </span>
                      <span className="text-[#2d6b38]/50 text-sm font-bold">15 min</span>
                    </div>
                    <div className="flex items-center justify-between bg-[#f4f9ee] border border-[#e2ead8] rounded-2xl px-5 py-4">
                      <span className="font-extrabold text-[#1a3d20] text-lg">
                        {t('access.kikugawa_ic')}
                      </span>
                      <span className="text-[#2d6b38]/50 text-sm font-bold">20 min</span>
                    </div>
                  </div>
                </div>

                {/* BUS */}
                <div className="bg-white border border-[#dfe7d5] rounded-[24px] p-6 shadow-sm">
                  <p className="text-[10px] font-extrabold tracking-[.22em] uppercase text-[#1a3d20]/40 mb-4">
                    {isJa ? '公共交通機関' : 'Public Transport'}
                  </p>
                  <div className="bg-[#f4f9ee] border border-[#e2ead8] rounded-2xl p-5">
                    <p className="text-[#1a3d20] font-bold leading-[1.9] text-sm">
                      {t('access.bus_desc')}
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}